
import { supabase } from "@/integrations/supabase/client";
import { NewsArticle } from "@/types/news";
import { cleanText, decodeEntities } from "@/lib/text";

interface SearchFilters {
  sources: string[];
  categories: string[];
  dateRange: string;
}

// Transform database article to NewsArticle format
const transformArticle = (dbArticle: any): NewsArticle => ({
  id: dbArticle.id,
  title: cleanText(dbArticle.title),
  excerpt: cleanText(dbArticle.excerpt),
  content: decodeEntities(dbArticle.content),
  url: dbArticle.url,
  imageUrl: dbArticle.image_url,
  publishedAt: dbArticle.published_at,
  source: {
    id: dbArticle.source_id,
    name: cleanText(dbArticle.source_name),
    url: dbArticle.source_url,
    logoUrl: dbArticle.source_logo_url
  },
  author: dbArticle.author_name ? {
    name: cleanText(dbArticle.author_name),
    avatar: dbArticle.author_avatar
  } : undefined,
  category: cleanText(dbArticle.category) || 'General',
  readTime: dbArticle.read_time || '5 min read'
});


// Fallback database search for when cache is not available
export const searchArticles = async (query: string, filters: SearchFilters): Promise<NewsArticle[]> => {
  try {
    let supabaseQuery = supabase
      .from('articles')
      .select('*')
      .order('published_at', { ascending: false });

    // Add full-text search if query is provided
    if (query.trim()) {
      supabaseQuery = supabaseQuery.or(
        `title.ilike.%${query}%,content.ilike.%${query}%,excerpt.ilike.%${query}%`
      );
    }

    // Filter by sources
    if (filters.sources.length > 0) {
      supabaseQuery = supabaseQuery.in('source_name', filters.sources);
    }

    // Filter by categories
    if (filters.categories.length > 0) {
      supabaseQuery = supabaseQuery.in('category', filters.categories);
    }

    // Filter by date range with more options
    if (filters.dateRange !== 'all') {
      const now = new Date();
      let startDate: Date;

      switch (filters.dateRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case '3days':
          startDate = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '2weeks':
          startDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case '3months':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;
        default:
          startDate = new Date(0);
      }

      supabaseQuery = supabaseQuery.gte('published_at', startDate.toISOString());
    }

    const { data, error } = await supabaseQuery.limit(100);

    if (error) {
      console.error('Search error:', error);
      throw error;
    }

    return (data || []).map(transformArticle);
  } catch (error) {
    console.error('Error searching articles:', error);
    throw error;
  }
};

export const getAvailableSources = async (): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from('articles')
      .select('source_name')
      .not('source_name', 'is', null);

    if (error) throw error;

    // Use Set to get unique sources more efficiently
    const uniqueSources = [...new Set(data?.map(item => item.source_name).filter(Boolean) || [])];
    return uniqueSources.sort();
  } catch (error) {
    console.error('Error fetching sources:', error);
    return [];
  }
};

export const getAvailableCategories = async (): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from('articles')
      .select('category')
      .not('category', 'is', null);

    if (error) throw error;

    // Use Set to get unique categories more efficiently
    const uniqueCategories = [...new Set(data?.map(item => item.category).filter(Boolean) || [])];
    return uniqueCategories.sort();
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

// Enhanced search suggestions using database full-text search
export const getSearchSuggestions = async (query: string): Promise<string[]> => {
  if (query.length < 2) return [];
  
  try {
    const { data, error } = await supabase
      .from('articles')
      .select('title')
      .textSearch('title', query, {
        type: 'websearch',
        config: 'english'
      })
      .limit(5);

    if (error) throw error;

    // Extract meaningful keywords from titles
    const suggestions = data?.map(article => {
      const words = article.title.toLowerCase().split(' ');
      return words.find(word => word.includes(query.toLowerCase()) && word.length > 2);
    }).filter((s): s is string => Boolean(s)) || [];

    return [...new Set(suggestions)].slice(0, 5);
  } catch (error) {
    console.error('Error fetching search suggestions:', error);
    return [];
  }
};

// Search history management (local storage)
export const saveSearchToHistory = (query: string, filters: SearchFilters) => {
  if (!query.trim()) return;
  
  const history = getSearchHistory();
  const searchItem = {
    query,
    filters,
    timestamp: new Date().toISOString()
  };
  
  // Remove existing entry if it exists
  const filteredHistory = history.filter((item: { query: string }) => item.query !== query);
  
  // Add new entry at the beginning
  const newHistory = [searchItem, ...filteredHistory].slice(0, 10); // Keep last 10 searches
  
  localStorage.setItem('search_history', JSON.stringify(newHistory));
};

export const getSearchHistory = () => {
  try {
    const history = localStorage.getItem('search_history');
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Error getting search history:', error);
    return [];
  }
};

export const clearSearchHistory = () => {
  localStorage.removeItem('search_history');
};
